<template>
  <ManageSocialConnectionsTemplate
    :is-loading="hashtagIsLoading"
    :pagination="hashtagPagination"
    @pagination-changed="emit('pagination-changed', $event)"
  >
    <template #header>
      <div class="tw:basis-1/2">
        <InputText
          v-model="addHashtag.current"
          data-testid="hashtag-input"
          :disabled="!hashtagCanBeAdded"
          new-design
          optional
          :placeholder="
            getLocaleText(
              'manage_social_connections.add_new_hashtag_placeholder'
            )
          "
          @submit="handleAddNewHashtag"
        >
          <template #label>
            <div class="field-heading tw:mb-2">
              {{ getLocaleText("manage_social_connections.add_new_hashtag") }}
            </div>
          </template>
        </InputText>

        <StreemTooltip v-if="hashtagExceeded">
          {{
            getLocaleText("manage_social_connections.hashtags_exceeded_tooltip")
          }}
        </StreemTooltip>
      </div>

      <div class="tw:basis-1/2 tw:pl-2">
        <InputText
          v-model="searchHashtag.current"
          clearable
          data-testid="search-input"
          debounce="300"
          :disabled="!hasHashtags"
          new-design
          :placeholder="
            getLocaleText(
              'manage_social_connections.search_hashtags_placeholder'
            )
          "
          @clear="handleSearchClear"
          @keydown="handleSearchKeydown"
          @submit="handleSearchSubmit"
        >
          <template #label>
            <div class="field-heading tw:mb-2">
              {{ getLocaleText("manage_social_connections.search_hashtags") }}
            </div>
          </template>
        </InputText>
      </div>

      <ManageSocialConnectionsHashtagsUsed
        v-if="hasHashtags"
        :count="hashtagTotalCount"
      />

      <div
        v-if="hasHashtags"
        class="tw:flex tw:w-full tw:pl-2"
      >
        <SelectAllCheckboxButton
          :disabled="!hashtagIsEnabled"
          :indeterminate="isIndeterminate"
          :model-value="isAllSelected"
          :page-size="hashtagTotalCount"
          render-delete-button
          :selected="hashtagSelectedIds.size"
          @delete-all="emit('delete')"
          @update:model-value="emit('toggle-select-all', $event)"
        />
      </div>
    </template>

    <template #default>
      <ManageSocialConnectionsHashtagCard
        v-for="[id, hashtag] in hashtagCollection"
        :id="id"
        :key="`hashtag-${id}`"
        class="tw:mb-2"
        :instagram-hashtag="hashtag"
        @checkbox-clicked="handleCheckboxClick"
        @delete-clicked="emit('delete', $event)"
      />
    </template>

    <template #no-data>
      <BaseIcon
        class="tw:text-5xl"
        icon="hashtag-search"
      />

      <div class="tw:text-base tw:text-denim-700">
        <i18n-t
          class="tw:text-base tw:text-denim-700"
          :keypath="
            hasHashtags
              ? 'manage_social_connections.no_hashtags_found'
              : 'manage_social_connections.get_started_hashtag'
          "
          scope="global"
        >
          <template #search>
            <span class="tw:italic">{{ searchHashtag.previous }}</span>
          </template>
          <template #newline>
            <br />
          </template>
        </i18n-t>
      </div>
    </template>
  </ManageSocialConnectionsTemplate>
</template>

<script lang="ts" setup>
import { computed, inject, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseIcon, InputText, StreemTooltip } from "shared/components/base";
import { type InputTextSubmitEvent } from "shared/components/base/InputText.vue";
import {
  ManageSocialConnectionsHashtagCard,
  ManageSocialConnectionsHashtagsUsed,
} from "shared/components/integrations/ManageSocialConnections";
import SelectAllCheckboxButton from "shared/components/integrations/SelectAllCheckboxButton.vue";
import { type PaginationData } from "shared/components/Pagination";
import { useSelectionState } from "shared/composables/useSelectionState";
import { isInstagramHashtag } from "shared/composables/useValidation";
import { useValueWithPrevious as useValue } from "shared/composables/useValueWithPrevious";
import { type Nullable } from "shared/types";
import { isNonNullable } from "shared/types/guards";

import ManageSocialConnectionsTemplate from "./ManageSocialConnectionsTemplate.vue";
import { type HashtagId } from "./types";
import {
  type UseHashtagContextReturn,
  HASHTAG_CONTEXT,
} from "./useHashtagContext";
import { useSocialConnectionsSearch } from "./useSocialConnectionsSearch";

interface Emits {
  "add-hashtag": [Nullable<string>];
  "add-hashtag-error": [error: string];
  delete: [id?: HashtagId];
  "pagination-changed": [pagination: PaginationData];
  "search-hashtag": [string];
  "toggle-item-selection": [id: HashtagId, selected: boolean];
  "toggle-select-all": [selected: boolean];
}

const emit = defineEmits<Emits>();

const {
  filteredHashtagCollection,
  hasHashtags,
  hashtagCanBeAdded,
  hashtagCollection,
  hashtagExceeded,
  hashtagIsEnabled,
  hashtagIsLoading,
  hashtagNames,
  hashtagPagination,
  hashtagSelectedIds,
  hashtagTotalCount,
} = inject<UseHashtagContextReturn>(HASHTAG_CONTEXT)!;

const { value: addHashtag } = useValue<string>("");

const {
  handleSearchClear,
  handleSearchKeydown,
  handleSearchSubmit,
  searchValue: searchHashtag,
  searchWatcher,
} = useSocialConnectionsSearch({
  onSearch: (query) => emit("search-hashtag", query),
});

const { isAllSelected, isIndeterminate } = useSelectionState({
  allItems: filteredHashtagCollection,
  pagination: hashtagPagination,
  selectedIds: hashtagSelectedIds,
});

const errorDuplicateHashtag = computed(() =>
  getLocaleText("manage_social_connections.error_duplicate_hashtag")
);

function handleAddNewHashtag({ event, value }: InputTextSubmitEvent): void {
  if (event?.key !== "Enter") return;
  const trimmedValue = value ? value.trim() : "";

  const isValid = (name: string) => isInstagramHashtag(name) === true;

  if (hashtagNames.value.has(trimmedValue)) {
    emit("add-hashtag-error", errorDuplicateHashtag.value);

    return;
  }

  if (isNonNullable(trimmedValue) && isValid(trimmedValue)) {
    emit("add-hashtag", trimmedValue);
    addHashtag.value.current = "";
  } else {
    (event.currentTarget as HTMLInputElement).select();
    emit("add-hashtag", null);
  }
}

function handleCheckboxClick(id: string, isSelected: boolean): void {
  emit("toggle-item-selection", id, isSelected);
}

watch(() => searchHashtag.value.current, searchWatcher);

defineExpose({ searchHashtag });
</script>
